import React, {FC, useEffect, useState} from 'react'
import InputField from '../components/InputField'
import {IProductProps, productActionType} from '../cors/reducers'
import {
  getBrands,
  getColors,
  getGenders,
  getLengthClasses,
  getRecommendedAges, getSizes,
  getWeightClasses,
} from '../cors/_requests'
import SharedData from '../../common/SharedData'
import {
  IBrandModel,
  IColorsModel,
  IGenderModel,
  ILengthClassesModel,
  IRecommendedAgeModel,
  ISelectOptions, ISizesModel,
  IWeightClassesModel,
} from '../cors/_models'
import {useIntl} from 'react-intl'
import ProductPriceTab from "./ProductPriceTab";
import {isShopUser} from '../../common/stockScope'

const ProductDataTab: FC<IProductProps> = ({prodState, prodDispatch, isChecked }) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const [lengthClasses, setLengthClasses] = useState<ISelectOptions[]>([])
  const [weightClasses, setWeightClasses] = useState<ISelectOptions[]>([])
  const [recommendedAges, setRecommendedAges] = useState<ISelectOptions[]>([])
  const [gender, setGender] = useState<ISelectOptions[]>([])
  const [colors, setColors] = useState<any[]>([])
  const [availableSizes, setAvailableSizes] = useState<any[]>([]);

  const [brand, setBrand] = useState<ISelectOptions[]>([])
  const currentDate = new Date().toISOString().split('T')[0];
  // A partner shop is never asked for an expiry date.
  const shopUser = isShopUser(currentUser)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { id: string; value: string } }) => {
    const {id, value} = e.target
    prodDispatch({type: productActionType.text, payload: {id, value}})
  }

  useEffect(() => {
    const getLengthClass = async () => {
      const {data} = await getLengthClasses(currentUser?.token, language)
      const convertData = data.map((item: ILengthClassesModel) => {
        return {label: item.name, value: item.length_class_id}
      })

      setLengthClasses(convertData)
    }
    const getWeightClass = async () => {
      const {data} = await getWeightClasses(currentUser?.token, language)

      const convertData = data.map((item: IWeightClassesModel) => {
        return {label: item.name, value: item.weight_class_id}
      })

      setWeightClasses(convertData)
    }
    const getColorsData = async () => {
      const {data} = await getColors(currentUser?.token, language)

      const convertData = data.map((item: IColorsModel) => {
        return {label: item.name, value: item.color_id, color: `#${item.color_id}`}
      })

      setColors(convertData)
    }
    const getRecommendedAgesData = async () => {
      const {data} = await getRecommendedAges(currentUser?.token, language)

      const convertData = data.map((item: IRecommendedAgeModel) => {
        return {label: item.name, value: item.recommended_age_id}
      })

      setRecommendedAges(convertData)
    }

    const getGenderData = async () => {
      const {data} = await getGenders(currentUser?.token, language)

      const convertData = data.map((item: IGenderModel) => {
        return {label: item.name, value: item.gender_id}
      })

      setGender(convertData)
    }
    const getBrandsData = async () => {
      const {data} = await getBrands(currentUser?.token, language)
      const convertData = data.map((item: IBrandModel) => {
        return {label: item.name, value: item.manufacturer_id}
      })

      setBrand([
        { label: '', value: '' },
        { label: formatMessage({ id: 'no_brand' }), value: '-1' },
        ...convertData
      ]);
    }

    const fetchSizes = async () => {
      const { data } = await getSizes(currentUser?.token, language);
      const sizesOptions = data.map((item: ISizesModel) => ({
        label: item.name,
        value: item.size_id
      }));
      setAvailableSizes(sizesOptions);
    };

    getLengthClass()
    getWeightClass()
    getColorsData()
    getRecommendedAgesData()
    getGenderData()
    getBrandsData()
    fetchSizes();
  }, [])
  const handleColorChange = (e: { target: { id: string; value: string } }) => {
    prodDispatch({type: productActionType.text, payload: {id: 'color', value: e.target.value}})
  }

  const handleSizeChange = (e: { target: { id: string; value: string } }) => {
    prodDispatch({type: productActionType.text, payload: {id: 'size', value: e.target.value}})
  }

  return (
    <div className='card default-image-box'>
      <div>
        <>
          <div className='d-flex flex-column gap-9'>
            {/* brandName */}
            <div className='row'>
              <div className='col-md-6'>
                <label className='form-label fw-bold fs-3'>
                  {formatMessage({ id: "brand_manu" })}
                  <sup className='text-danger'>*</sup>
                </label>
                <InputField
                    id='manufacturer_id'
                    type='select'
                    options={brand}
                    onChange={handleChange}
                    isRequired={true}
                    hasError={prodState.manufacturer_id.error}
                    value={prodState.manufacturer_id.value}
                    isSearchable={true}
                />
              </div>
              <div className='col-md-6 d-flex gap-10px'>
                <div className='width-50P'>
                  <label className='form-label fw-bold fs-3'>
                    {formatMessage({ id: "recommended_age" })}
                  </label>
                  <InputField
                      hasError={prodState.recommendedAge.error}
                      value={prodState.recommendedAge.value}
                      isRequired={false}
                      id='recommendedAge'
                      type='select'
                      options={[{label: formatMessage({ id: "recommended_age" }), value: ''}, ...recommendedAges]}
                      onChange={handleChange}
                      isSearchable={true}
                  />
                </div>
                <div className='width-50P'>
                  <label className='form-label fw-bold fs-3'>{formatMessage({ id: "gender" })}</label>
                  <InputField
                      id='gender'
                      type='select'
                      value={prodState.gender}
                      onChange={handleChange}
                      options={[{label: formatMessage({ id: "select_gender" }), value: ''}, ...gender]}
                      isSearchable={true}
                  />
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-md-6'>

            {/* Dimension (L x W x H) */}
                <div className='w-100'>
                  <label className='form-label fw-bold fs-3'>{formatMessage({ id: "prod_dimensions" })}</label>
                  <div className='d-flex gap-10px'>
                    <div className='d-flex width-50P'>
                      <div className='d-flex gap-10px'>
                        <div style={{width: '33%'}}>
                          <InputField
                            id='dimensionL'
                            value={prodState.dimensionL}
                            onChange={handleChange}
                          />
                        </div>
                        <div style={{width: '33%'}}>
                          <InputField
                            id='dimensionW'
                            value={prodState.dimensionW}
                            onChange={handleChange}
                          />
                        </div>
                        <div style={{width: '33%'}}>
                          <InputField
                            id='dimensionH'
                            value={prodState.dimensionH}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-3' style={{width: '50%'}}>
                      <InputField
                        type='select'
                        id='lengthClass'
                        value={prodState.lengthClass}
                        onChange={handleChange}
                        options={[{label: formatMessage({ id: "length_class" }), value: ''}, ...lengthClasses]}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Weight  */}
              <div className='col-md-6'>
                <div className='w-100'>
                  <label className='form-label fw-bold fs-3'>{formatMessage({ id: "weight" })}</label>
                  <div className='d-flex gap-10px'>
                    <div className='width-50P'>
                      <InputField id='weight' value={prodState.weight} onChange={handleChange} />
                    </div>
                    <div className='width-50P'>
                      <InputField
                          type='select'
                          id='weightClass'
                          value={prodState.weightClass}
                          onChange={handleChange}
                          options={[{label: formatMessage({ id: "weight_class" }), value: ''}, ...weightClasses]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>



            <div className='row'>

              <div className='col-md-6'>
                <label className='form-label fw-bold fs-3'>{formatMessage({ id: "youtube_URL" })}</label>
                <InputField id='youtubeUrl' value={prodState.youtubeUrl} onChange={handleChange} />
              </div>
              {!shopUser && (
                <div className='col-md-6'>
                  <label className='form-label fw-bold fs-3'>{formatMessage({ id: "expiry_date" })}</label>
                  <InputField
                      type="date"
                      id='expiryDate'
                      value={prodState.expiryDate.value === '0000-00-00' || prodState.expiryDate.value === '0000-00-00 00:00:00' ? '' : prodState.expiryDate.value}
                      onChange={handleChange}
                      min={currentDate}
                      isRequired={false}
                      hasError={prodState.expiryDate.error}
                      errorMsg={prodState.expiryDate.msg}
                  />
                </div>
              )}
            </div>

            <div className='row'>
              <div className='col-md-6'>
                <ProductPriceTab prodState={prodState} prodDispatch={prodDispatch} isChecked={isChecked}/>
              </div>

                  <div className='col-md-6 d-flex gap-10px'>
                    {(!isChecked && prodState.variations.length === 0) &&

                    <div className='d-flex gap-10px width-100P'>
                      <div className='width-50P'>
                        <div>
                          <label className='form-label fw-bold fs-3'>{formatMessage({ id: "color" })}</label>
                          <InputField
                              id='color'
                              type='select'
                              value={prodState.color}
                              onChange={handleColorChange}
                              options={colors}
                              isSearchable={true}
                          />
                        </div>
                      </div>
                      <div className='width-50P'>

                        <label className="form-label fw-bold fs-3">
                          {formatMessage({ id: "size" })}
                        </label>
                        <div>
                          <InputField
                              id='size'
                              type='select'
                              value={prodState.size}
                              onChange={handleSizeChange}
                              options={availableSizes}
                              isSearchable={true}
                          />

                        </div>

                      </div>

                    </div>
                    }
                  </div>
            </div>

          </div>
        </>
      </div>
    </div>
  )
}

export default ProductDataTab
